import { useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useToast } from '../lib/toast'
import { Thumb } from './Thumb'
import { MealEditor } from './MealEditor'
import { componentLine, groupComponents, mealMatches } from '../lib/meals'
import type { Bootstrap, Meal } from '@shared/types'

interface Props {
  boot: Bootstrap
  onMealsChanged: (meals: Meal[]) => void
  onError: (err: unknown, fallback: string) => void
}

/** "Packed 4 times · last on 12 Sep" -- how much rotation a meal is really in. */
function usageLine(meal: Meal): string {
  if (meal.useCount === 0) return 'Not packed yet'
  const times = `Packed ${meal.useCount} time${meal.useCount === 1 ? '' : 's'}`
  if (!meal.lastUsedAt) return times
  // The server stores UTC as "YYYY-MM-DD HH:MM:SS"; only the date is shown.
  const [date] = meal.lastUsedAt.split(' ')
  return `${times} · last on ${date}`
}

/** Manage the repertoire: the combinations that already work, by name. */
export function MealsPage({ boot, onMealsChanged, onError }: Props) {
  const toast = useToast()
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<{ meal: Meal | null } | null>(null)

  const itemsById = useMemo(() => new Map(boot.items.map((i) => [i.id, i])), [boot.items])

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return boot.meals.filter((m) => mealMatches(m, itemsById, needle))
  }, [boot.meals, itemsById, query])

  async function toggleFavorite(meal: Meal) {
    try {
      onMealsChanged(await api.updateMeal(meal.id, { favorite: !meal.favorite }))
    } catch (err) {
      onError(err, 'Could not update that meal.')
    }
  }

  async function duplicate(meal: Meal) {
    try {
      const { meals } = await api.createMeal({
        name: `${meal.name} copy`,
        notes: meal.notes,
        emoji: meal.emoji,
        itemIds: meal.itemIds,
      })
      onMealsChanged(meals)
      toast.notify(`Copied ${meal.name}. Edit the copy to change it.`)
    } catch (err) {
      onError(err, 'Could not copy that meal.')
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Meals</h1>
          <p>
            Whole combinations that already work. Applying one fills a day&rsquo;s slots in a single
            tap, from the ⭐ on any day of the plan.
          </p>
        </div>
        <div className="spacer" />
        <button className="btn primary" onClick={() => setEditing({ meal: null })}>
          + New meal
        </button>
      </div>

      {boot.meals.length > 6 && (
        <input
          className="pantry-search"
          style={{ maxWidth: 300 }}
          type="search"
          autoComplete="off"
          placeholder="Search saved meals…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search saved meals"
        />
      )}

      {boot.meals.length === 0 ? (
        <div className="empty">
          <span className="big" aria-hidden="true">
            🍱
          </span>
          <p>No saved meals yet.</p>
          <p className="muted" style={{ fontSize: 13.5 }}>
            Save one from a day you have already built — the ⭐ on any day of the plan — or start one
            from scratch here.
          </p>
          <div className="row" style={{ justifyContent: 'center', marginTop: 14 }}>
            <button className="btn primary" onClick={() => setEditing({ meal: null })}>
              Build a meal
            </button>
          </div>
        </div>
      ) : shown.length === 0 ? (
        <p className="muted">Nothing matches “{query}”.</p>
      ) : (
        <div className="meal-grid">
          {shown.map((meal) => (
            <div className={`meal-card ${meal.favorite ? 'favorite' : ''}`} key={meal.id}>
              <div className="meal-card-head">
                <button
                  type="button"
                  className={`star ${meal.favorite ? 'on' : ''}`}
                  onClick={() => toggleFavorite(meal)}
                  aria-pressed={meal.favorite}
                  aria-label={
                    meal.favorite ? `Unfavourite ${meal.name}` : `Make ${meal.name} a favourite`
                  }
                >
                  {meal.favorite ? '★' : '☆'}
                </button>
                <span className="title">
                  {meal.emoji && <span aria-hidden="true">{meal.emoji} </span>}
                  {meal.name}
                </span>
                <div className="spacer" />
                <button className="btn sm ghost" onClick={() => duplicate(meal)} title="Make a copy">
                  Copy
                </button>
                <button className="btn sm" onClick={() => setEditing({ meal })}>
                  Edit
                </button>
              </div>

              {meal.notes && <p className="meal-card-notes">{meal.notes}</p>}

              <div className="meal-card-slots">
                {groupComponents(meal, itemsById, boot.categories).map(({ category, items }) => (
                  <div className="meal-card-slot" key={category.id}>
                    <span className="slot-dot" style={{ background: category.color }} aria-hidden="true" />
                    <span className="cat">{category.name}</span>
                    <span className="picks">
                      {items.map((item) => (
                        <span className="pick" key={item.id}>
                          <Thumb emoji={item.emoji} imageKey={item.imageKey} name={item.name} />
                          {item.name}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
                {componentLine(meal, itemsById) === '' && (
                  <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                    Everything in this meal has been deleted from the pantry. Edit it to rebuild it.
                  </p>
                )}
              </div>

              <div className="meal-card-foot muted">{usageLine(meal)}</div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <MealEditor
          meal={editing.meal}
          categories={boot.categories}
          items={boot.items}
          onSaved={onMealsChanged}
          onClose={() => setEditing(null)}
          onError={onError}
        />
      )}
    </>
  )
}
